import { downloadByUrl } from '@insurancemenu/imenu-components-lib/dist/helpers/downloadByUrl';
import { AcceptedFiles, SupportedUploadTypes } from '@insurancemenu/imenu-components-lib/dist/types/FileUpload';
import { action, computed, makeObservable, observable } from 'mobx';
import { getRootStoreInstance } from 'store/rootStore';
import { ExistingCensusInfo } from 'types/censusService';

export interface CensusModuleDependencies {
  groupId: string;
  enrollmentPeriodId: string;
}

export abstract class ICensusModule<DomainDependencies extends CensusModuleDependencies> {
  private _censusHasErrors: boolean = false;
  private _previouslyUploadedFilesUrls: string[] | null = null;
  private _isLoading: boolean = false;
  protected _existingCensusInfo: ExistingCensusInfo | null = null;
  protected _rootStore = getRootStoreInstance();
  protected _domainDependencies: DomainDependencies;
  protected _uploadedFiles: AcceptedFiles | null = null;

  protected _setPreviouslyUploadedFilesUrls(urls: string[]): void {
    this._previouslyUploadedFilesUrls = urls;
  }

  public get domainDependencies(): DomainDependencies {
    return this._domainDependencies;
  }

  public get allFilesInOneFormat(): boolean {
    if (!this._uploadedFiles || Object.keys(this._uploadedFiles).length < 1) {
      return true;
    }
    return [...new Set(Object.keys(this._uploadedFiles).map((name) => name.split('.').pop()))].length === 1;
  }

  public get censusHasErrors(): boolean {
    return this._censusHasErrors;
  }

  public get noUploadedFiles(): boolean {
    return this._uploadedFiles === null || Object.keys(this._uploadedFiles).length < 1;
  }

  public get fileFormat(): SupportedUploadTypes | null {
    const firstFileType = this._uploadedFiles && Object.keys(this._uploadedFiles)[0]?.split('.').pop();
    if (firstFileType && this.allFilesInOneFormat) {
      return firstFileType as SupportedUploadTypes;
    }
    return null;
  }

  public get isLoading(): boolean {
    return this._isLoading;
  }

  public get previouslyUploadedFilesUrls(): string[] | null {
    return this._previouslyUploadedFilesUrls;
  }

  public get existingCensusInfo(): ExistingCensusInfo | null {
    return this._existingCensusInfo;
  }

  public setIsLoading(isLoading: boolean): void {
    this._isLoading = isLoading;
  }

  public setCensusHasErrors(hasErrors: boolean): void {
    this._censusHasErrors = hasErrors;
  }

  public setFiles(acceptedFiles: AcceptedFiles): void {
    this._uploadedFiles = acceptedFiles;
  }

  public downloadPreviousFiles(): void {
    this._previouslyUploadedFilesUrls?.forEach((url) => {
      downloadByUrl(url);
    });
  }

  abstract uploadCensus(): Promise<void>;
  abstract uploadExistingCensus(): Promise<void>;
  abstract getPreviouslyUploadedFiles(): Promise<void>;
  abstract downloadCensusTemplate(): Promise<void>;

  constructor(domainDependencies: DomainDependencies) {
    this._domainDependencies = domainDependencies;
    makeObservable<
      ICensusModule<DomainDependencies>,
      | '_rootStore'
      | '_domainDependencies'
      | '_censusHasErrors'
      | '_uploadedFiles'
      | '_previouslyUploadedFilesUrls'
      | '_isLoading'
      | '_existingCensusInfo'
      | '_setPreviouslyUploadedFilesUrls'
    >(this, {
      _rootStore: observable,
      _domainDependencies: observable,
      _censusHasErrors: observable,
      _uploadedFiles: observable,
      _previouslyUploadedFilesUrls: observable,
      _isLoading: observable,
      _existingCensusInfo: observable,
      _setPreviouslyUploadedFilesUrls: action.bound,
      domainDependencies: computed,
      allFilesInOneFormat: computed,
      censusHasErrors: computed,
      noUploadedFiles: computed,
      fileFormat: computed,
      isLoading: computed,
      previouslyUploadedFilesUrls: computed,
      existingCensusInfo: computed,
      setIsLoading: action.bound,
      setCensusHasErrors: action.bound,
      setFiles: action.bound,
      downloadPreviousFiles: action.bound,
    });
  }
}
